// Ansicht Audit (Issue #82).
//
// Rendert den Audit-Trail eines Projekts: die verkettete Ereignisliste (roh, da das Backend die
// Einträge frei geformt liefert) plus ein verify-Abzeichen — grün „Kette intakt" oder rot
// „gebrochen bei Seq N". „Audit exportieren" lädt die entries client-seitig als .jsonl herunter
// (eine JSON-Zeile je Eintrag, Blob-Download).

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { getAudit } from '../api/client';
import type { AuditResponse } from '../api/types';
import { Button, EmptyState, PanelHeading } from '../components/ui';

// Baut aus den Einträgen eine .jsonl-Datei (eine JSON-Zeile je Eintrag) und stößt den Download an.
function downloadJsonl(projectId: string, entries: Record<string, unknown>[]): void {
  const body = entries.map((e) => JSON.stringify(e)).join('\n');
  const blob = new Blob([body], { type: 'application/x-ndjson' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${projectId}.jsonl`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function AuditView(): JSX.Element {
  const { t } = useI18n();
  const { activeProjectId, reloadNonce, reportError } = useStore();

  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (projectId: string) => {
      setLoading(true);
      try {
        setAudit(await getAudit(projectId));
      } catch (err) {
        reportError(err, t('audit.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [reportError, t],
  );

  // Bei Projektwechsel laden; reloadNonce triggert ein Neuladen nach einem Revert aus der Historie.
  useEffect(() => {
    if (!activeProjectId) {
      setAudit(null);
      return;
    }
    void load(activeProjectId);
  }, [activeProjectId, reloadNonce, load]);

  const doExport = useCallback(() => {
    if (!activeProjectId || !audit) return;
    downloadJsonl(activeProjectId, audit.entries);
  }, [activeProjectId, audit]);

  if (!activeProjectId) {
    return (
      <div className="h-full p-6">
        <EmptyState title={t('audit.empty')} hint={t('audit.emptyHint')} />
      </div>
    );
  }

  const verify = audit?.verify;
  const broken = verify != null && !verify.valid;

  return (
    <div className="flex h-full flex-col overflow-auto p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <PanelHeading>{t('audit.title')}</PanelHeading>
          <p className="max-w-3xl text-xs text-bf-muted">{t('audit.hint')}</p>
        </div>
        <Button
          onClick={doExport}
          disabled={!audit || audit.entries.length === 0}
          testId="audit-export"
        >
          {t('audit.export')}
        </Button>
      </div>

      {verify && (
        <div className="mb-4 flex items-center gap-3">
          <span
            data-testid="audit-verify-badge"
            data-valid={verify.valid ? 'true' : 'false'}
            className={
              'rounded px-2 py-1 text-xs font-semibold ' +
              (broken
                ? 'border border-red-500/60 bg-red-500/10 text-red-300'
                : 'border border-emerald-500/60 bg-emerald-500/10 text-emerald-300')
            }
          >
            {broken
              ? t('audit.chainBroken', { seq: verify.brokenAt ?? '?' })
              : t('audit.chainOk')}
          </span>
          <span className="text-xs text-bf-muted">
            {t('audit.length', { count: verify.length })}
          </span>
        </div>
      )}

      {audit && audit.entries.length === 0 && !loading ? (
        <EmptyState title={t('audit.noEntries')} />
      ) : (
        <ol className="flex flex-col gap-1" data-testid="audit-entries">
          {(audit?.entries ?? []).map((entry, i) => (
            <li
              key={i}
              data-testid={`audit-entry-${i}`}
              className="rounded border border-bf-border bg-bf-navy-light px-3 py-2"
            >
              <pre className="overflow-auto text-xs leading-relaxed text-bf-white">
                {JSON.stringify(entry, null, 2)}
              </pre>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
